'use client'

import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { navigateUrl } from '@/utils/client-request'
import { ChevronLeft } from 'lucide-react'
import Shopmn from '@/assets/logo-black.svg'

type Panel = { bg: string, eyebrow: string, headline: React.ReactNode, sub: string }

const PANELS: Record<string, Panel> = {
  login: {
    bg: 'linear-gradient(150deg, #FFD9E7 0%, #E8E2FF 100%)',
    eyebrow: 'Shop.mn · Аюулгүй',
    headline: <>Дахин<br />тавтай<br /><span className="italic" style={{ background: 'linear-gradient(90deg, #2B7FFF, #7C86FF)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>морил.</span></>,
    sub: 'Хүслийн жагсаалт, захиалгын түүх, үнэлгээ сэтгэгдэл. Бүгд таныг хүлээж байна.',
  },
  register: {
    bg: 'linear-gradient(155deg, #FFE0C9 0%, #FFD9E7 100%)',
    eyebrow: 'Shop.mn · Шинэ бүртгэл',
    headline: <>Анхны<br />худалдан авалтаа<br /><span className="italic" style={{ background: 'linear-gradient(90deg, #2B7FFF, #7C86FF)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>хий.</span></>,
    sub: 'Бүртгэлээ үүсгээд, илүү хялбар бараагаа захиал.',
  },
  forgot: {
    bg: 'linear-gradient(160deg, #E8E2FF 0%, #DCEEF6 100%)',
    eyebrow: 'Shop.mn · Сэргээх',
    headline: <>Сэргээх<br />амар.</>,
    sub: 'Бүртгэлтэй хаяг руу 6 оронтой нэг удаагийн код илгээх болно.',
  },
  reset: {
    bg: 'linear-gradient(155deg, #FFE0C9 0%, #FEF08A 100%)',
    eyebrow: 'Shop.mn · Шинэ нууц үг',
    headline: <>Бараг<br />л боллоо.</>,
    sub: 'Шинэ нууц үг тохируулсны дараа таны сагс болон хүслийн жагсаалт хадгалагдсан хэвээр байх болно.',
  },
  logout: {
    bg: 'linear-gradient(155deg, #E8E2FF 0%, #FFE0C9 100%)',
    eyebrow: 'Shop.mn · Баяртай',
    headline: <>Дараа<br />дахин<br /><span className="italic" style={{ background: 'linear-gradient(90deg, #2B7FFF, #7C86FF)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>уулзъя.</span></>,
    sub: 'Та дараагийн худалдан авалтаа хийхэд бид танд тусална.',
  },
}

const pickPanel = (pathname: string): Panel => {
  if (pathname.includes('/login')) return PANELS.login
  if (pathname.includes('/register')) return PANELS.register
  if (pathname.includes('/forgot')) return PANELS.forgot
  if (pathname.includes('/reset')) return PANELS.reset
  if (pathname.includes('/logout')) return PANELS.logout
  return PANELS.login
}

export default function AuthLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname() || ''
  const panel = pickPanel(pathname)

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-cream font-gilroy">
      <div className="relative flex flex-col p-6 sm:p-10 lg:p-16">
        <a href={navigateUrl('/', pathname)} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-foreground/60 hover:text-foreground transition-colors w-fit">
          <ChevronLeft className="size-3.5" />
          Буцах
        </a>

        <div className="flex-1 flex items-center justify-center py-10">
          <div className="w-full max-w-md">
            {children}
          </div>
        </div>

        <div className="flex flex-col items-center gap-3 text-[13px] text-foreground/55">
          <Image src={Shopmn} alt="Shop.mn" className="h-5 w-auto opacity-60" />
          <div className="flex gap-5">
            <a href={navigateUrl('/service', pathname)} className="hover:text-foreground">Үйлчилгээний нөхцөл</a>
            <a href={navigateUrl('/payment', pathname)} className="hover:text-foreground">Төлбөр</a>
            <a href={navigateUrl('/delivery', pathname)} className="hover:text-foreground">Хүргэлт</a>
          </div>
        </div>
      </div>

      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden p-12 xl:p-16" style={{ background: panel.bg }}>
        <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="xMidYMid slice" viewBox="0 0 800 1000">
          <defs>
            <linearGradient id="auth-bubble-grad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#2B7FFF" stopOpacity="0.55" />
              <stop offset="1" stopColor="#7C86FF" stopOpacity="0.55" />
            </linearGradient>
          </defs>
          <circle cx="650" cy="120" r="220" fill="url(#auth-bubble-grad)" opacity="0.5" />
          <circle cx="120" cy="780" r="280" fill="url(#auth-bubble-grad)" opacity="0.35" />
          <circle cx="700" cy="900" r="160" fill="url(#auth-bubble-grad)" opacity="0.45" />
        </svg>

        <div className="relative inline-flex items-center gap-2 text-[12px] font-medium uppercase tracking-[0.14em] text-foreground/60">
          <span className="w-1.5 h-1.5 rounded-full bg-foreground/50" />
          {panel.eyebrow}
        </div>

        <div className="relative">
          <h2 className="font-bold font-gilroy text-foreground leading-[0.95] tracking-[-0.035em]" style={{ fontSize: 'clamp(3.5rem, 6vw, 5rem)' }}>
            {panel.headline}
          </h2>
          <p className="mt-6 text-[16px] sm:text-[17px] leading-[1.55] text-foreground/65 max-w-md">
            {panel.sub}
          </p>
        </div>

        <div className="relative flex items-center gap-3 text-[13px] text-foreground/55">
          <span className="font-semibold tracking-[0.08em]">01</span>
          <span className="flex-1 h-px bg-foreground/20" />
          <span className="tracking-[0.04em]">Хиймэл оюун ухаант цахим дэлгүүр</span>
        </div>
      </div>
    </div>
  )
}
